import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Truck, Radio, Settings, Gauge, Shield, Clock, Thermometer, Fuel } from 'lucide-react';
import { Counter } from '@/hooks/useCountUp';

const FleetPage = () => {
  const fleetStats = [
    { value: 100, suffix: '%', label: 'ADR Uyumlu', icon: Shield },
    { value: 100, suffix: '%', label: 'GPS Kapsama', icon: Radio },
    { value: 24, suffix: '/7', label: 'Kesintisiz İzleme', icon: Clock },
    { value: 99, suffix: '%', label: 'Operasyon Sürekliliği', icon: Gauge },
  ];

  const vehicleTypes = [
    { name: 'Akaryakıt Tankeri', capacity: '25.000 - 36.000 Litre', spec: 'Ana Operasyon', image: '/images/hortum-baglanti.webp', features: ['ADR Sertifikalı', 'Çok Bölmeli', 'Alt Dolum Sistemi'] },
    { name: 'Madeni Yağ Tankeri', capacity: '20.000 - 30.000 Litre', spec: 'Madeni Yağ Taşıma', image: '/images/operator-kontrol.webp', features: ['Isı Yalıtımlı Tank', 'Özel Pompa Sistemi', 'Çok Bölmeli'] },
    { name: 'Kimyasal Tanker', capacity: '20.000 - 30.000 Litre', spec: 'Özel Madde', image: '/images/istasyon-dolum.webp', features: ['Paslanmaz Çelik', 'Isı Yalıtımlı', 'ADR 3-8 Sınıf'] },
    { name: 'Özel Amaçlı', capacity: 'İhtiyaca Özel', spec: 'Endüstriyel', image: '/images/tanker-tam.webp', features: ['Havaalanı Yakıt', 'Jet Yakıt', 'Endüstriyel'] },
  ];

  const techFeatures = [
    { icon: Radio, title: 'Gerçek Zamanlı GPS Takip', description: 'Tüm araçlar saniye bazında izlenir. Konum bilgisi anlık olarak merkeze iletilir.' },
    { icon: Thermometer, title: 'Sıcaklık & Basınç İzleme', description: 'Tanker içi değerler sürekli izlenir. Anomaliler anında bildirilir.' },
    { icon: Fuel, title: 'Yakıt Tüketim Analizi', description: 'Araç bazında tüketim izlenir, optimizasyon sağlanır.' },
    { icon: Shield, title: 'Sürücü Davranış Analizi', description: 'Ani fren, hız aşımı izlenir. Güvenlik skorları oluşturulur.' },
    { icon: Settings, title: 'Otomatik Bakım Planlama', description: 'Kilometre ve kullanım verilerine göre bakım planları otomatik oluşturulur.' },
    { icon: Gauge, title: 'Performans Dashboard', description: 'Verimlilik ve maliyet analizleri tek panelden izlenir.' },
  ];

  return (
    <div data-testid="fleet-page" className="bg-white">
      <section className="page-hero-banner">
        <img src="/images/fleet-operations.webp" alt="" className="hero-bg" loading="eager" />
        <div className="hero-gradient" />
        <div className="hero-inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-block text-[#FF6B2C] text-xs font-semibold tracking-[0.2em] uppercase mb-4">Filo & Teknoloji</span>
            <h1 className="heading-xl text-white mb-4">Denetim altında filo. Kontrol altında süreç.</h1>
            <p className="text-base text-white/70 leading-relaxed max-w-lg">
              Dijital takip sistemleri ve düzenli bakım programıyla donatılmış profesyonel filo.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="trust-bar" data-testid="fleet-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6">
            {fleetStats.map((stat, index) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center">
                <stat.icon className="w-7 h-7 text-[#FF6B2C] mx-auto mb-3" />
                <div className="trust-number"><Counter end={stat.value} duration={2500} />{stat.suffix}</div>
                <div className="trust-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="section-label justify-center"><span>Araç Filosu</span></div>
            <h2 className="heading-lg text-[#0B3A82]">Her ihtiyaca özel, her araç denetim altında.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {vehicleTypes.map((vehicle, index) => (
              <motion.div key={vehicle.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl overflow-hidden hover:border-[#0B3A82]/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="h-44 sm:h-48 overflow-hidden">
                  <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover object-center" loading="lazy" />
                </div>
                <div className="p-6 sm:p-8">
                <h3 className="text-base font-bold text-[#1A1A2E] mb-2">{vehicle.name}</h3>
                <div className="text-sm text-[#64748B] mb-1">Kapasite: {vehicle.capacity}</div>
                <div className="text-sm text-[#FF6B2C] font-bold mb-4">{vehicle.spec}</div>
                <div className="space-y-2">
                  {vehicle.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-[#64748B]">
                      <div className="w-1.5 h-1.5 bg-[#0B3A82] rounded-full" />
                      {feature}
                    </div>
                  ))}
                </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-gray section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="section-label justify-center"><span>Teknoloji</span></div>
            <h2 className="heading-lg text-[#0B3A82]">Dijital kontrol. Şeffaf süreç.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techFeatures.map((feature, index) => (
              <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }}
                className="bg-white border border-[#E2E8F0] rounded-2xl p-8 hover:shadow-lg hover:border-[#0B3A82]/20 transition-all duration-300"
              >
                <div className="icon-box mb-5"><feature.icon className="w-5 h-5" /></div>
                <h3 className="text-base font-bold text-[#1A1A2E] mb-3">{feature.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="section-label"><span>Bakım & Denetim</span></div>
              <h2 className="heading-lg text-[#0B3A82] mb-5">Sürekli bakım. Kesintisiz güvenlik.</h2>
              <p className="text-body-lg mb-10">
                Tüm araçlarımız düzenli periyodik bakımdan geçer. Kendi bünyemizde bulunan
                servis ve bakım istasyonumuzda 7/24 teknik destek sağlanır.
              </p>
              <div className="space-y-4">
                {['Haftalık görsel kontrol ve denetim', 'Aylık periyodik bakım programı', 'Yıllık ADR ve muayene yenilemeleri', 'Anlık arıza tespit ve müdahale'].map((item, index) => (
                  <div key={index} className="flex items-center gap-3.5">
                    <div className="w-2 h-2 bg-[#FF6B2C] rounded-full flex-shrink-0" />
                    <span className="text-[#1A1A2E] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-10"
            >
              <h3 className="text-xl font-bold text-[#0B3A82] mb-8">Operasyonel Standartlar</h3>
              <div className="space-y-6">
                {[
                  { label: 'Araç Bakım Standardı', value: 'A+' },
                  { label: 'Periyodik Bakım Sıklığı', value: 'Aylık' },
                  { label: 'Arıza Müdahale Süresi', value: '< 2 Saat' },
                  { label: 'Operasyonel Verimlilik', value: '%99.2' },
                ].map((stat, index) => (
                  <div key={index} className="flex justify-between items-center border-b border-[#E2E8F0] pb-5 last:border-0">
                    <span className="text-[#64748B]">{stat.label}</span>
                    <span className="text-xl font-extrabold text-[#0B3A82]">{stat.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="cta-section section-padding-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="heading-lg text-white mb-5">Operasyonunuzu birlikte planlayalım.</h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-10 text-lg">Size özel çözümü konuşmak için ekibimiz hazır.</p>
            <Link to="/iletisim">
              <Button className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-10 py-6 text-base shadow-lg group" data-testid="fleet-page-cta-button">
                Ekibimizle görüşün
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FleetPage;
